import LayerDetailComponentVariantProperty
    from "../../apps/containers/AppContainer/BarrelContainer/LayerDetailContainer/LayerDetailComponentVariantProperty";

import {
    INDIVIDUAL_COMPONENTS_SECTION_DESCRIPTION,
    INDIVIDUAL_COMPONENTS_SECTION_NAME
} from "../../apps/containers/AppContainer/BarrelContainer/StyleguideContainer/constants";
import RawColor from "../api/model/colors/RawColor";
import ComponentPage, { ComponentPageType } from "../api/model/components/ComponentPage";
import ComponentProperty from "../api/model/components/ComponentProperty";
import ComponentVariant from "../api/model/components/ComponentVariant";
import ConnectedComponent from "../api/model/connectedComponents/ConnectedComponent";
import Barrel from "../model/Barrel";
import BarrelType from "../model/BarrelType";
import BasicComponentWithLayer from "../model/BasicComponentWithLayer";
import Component from "../model/Component";
import ComponentSection, { ComponentSectionWithDefaultInfo } from "../model/ComponentSection";
import ComponentSectionVariant from "../model/ComponentSectionVariant";
import InspectLayer from "../model/InspectLayer";
import Screen, { PartialScreen } from "../model/Screen";
import { ComponentSectionResult } from "../model/Section";
import StyleguideData from "../model/StyleguideData";

import { shallowDiffer } from "../utils/diff";
import { calculateMatchingScore } from "../utils/search";
import { sortBy } from "../utils/sortBy";

import BasicRecord from "./BasicRecord";
import { escapeRegExpButAsterisk } from "./escape";
import { exists } from "./object";

const LAYER_COMPONENT_VIEW_LIMIT = 50;
const HIGHLIGHTED_CONNECT_TO_CODE_VIEW_LIMIT = 50;

const SMALL_SIZE_COMPONENT_WIDTH = 48; // px
const SMALL_SIZE_COMPONENT_HEIGHT = 48; // px

type ComponentSectionsContainer = {
    componentSections?: ComponentSection[];
};

type BarrelLike = ComponentSectionsContainer & {
    id: string;
};

interface SearchedComponent extends Component {
    barrelId?: string;
    variantSectionName?: string;
}

interface ComponentQueryResult {
    component: SearchedComponent;
    ancestors: ComponentSection[];
}

interface ComponentSearchResult extends ComponentQueryResult {
    barrelId: string;
}

interface LayerComponentSearchResult extends ComponentSearchResult {
    layer: InspectLayer;
}

function flattenComponents(componentSection: Pick<ComponentSection, "components" | "componentSections">): Component[] {
    return componentSection.components.concat(
        ...componentSection.componentSections.map(cs => flattenComponents(cs))
    );
}

function flattenComponentSections(componentSections: ComponentSection[]): ComponentSection[] {
    return componentSections.flatMap(componentSection => [
        componentSection,
        ...flattenComponentSections(componentSection.componentSections)
    ]);
}

interface OverriddenPreviewBackgroundColorChildrenResult {
    pages: BasicRecord<RawColor>;
    componentSections: Map<string[], RawColor>;
}

function getOverriddenPreviewBackgroundColorChildren(barrel: Barrel, {
    pageId,
    csids
}: {
    pageId?: string;
    csids?: string[];
} = {}
): OverriddenPreviewBackgroundColorChildrenResult {
    let pages: ComponentPage[] = [];
    let componentSections: ComponentSection[] = [];

    if (pageId) {
        const page = barrel.pages?.find(pg => pg._id === pageId);

        if (page) {
            const csidMap = new Set(page.componentSections.map(cs => cs.id));
            componentSections = barrel.componentSections?.filter(cs => csidMap.has(cs._id)) ?? [];
        }
    } else if (csids) {
        const componentSection = barrel.findComponentSection(csids);

        if (componentSection) {
            ({ componentSections = [] } = componentSection);
        }
    } else {
        ({
            pages = [],
            componentSections = []
        } = barrel);
    }

    const overridenComponentSections = new Map(
        flattenComponentSections(componentSections)
            .filter((
                componentSection
            ): componentSection is ComponentSection & { previewBackgroundColor: RawColor; idPath: string[]; } =>
                componentSection.previewBackgroundColor !== null &&
                componentSection.previewBackgroundColor !== undefined &&
                componentSection.idPath !== undefined
            )
            .map(componentSection => [componentSection.idPath, componentSection.previewBackgroundColor])
    );

    const overridenPages = Object.fromEntries(
        pages
            .filter((page): page is ComponentPage & { previewBackgroundColor: RawColor; } =>
                page.previewBackgroundColor !== null &&
                page.previewBackgroundColor !== undefined
            ).map(page => [page._id, page.previewBackgroundColor])
    );

    return {
        pages: overridenPages,
        componentSections: overridenComponentSections
    };
}

function findAncestors(selectedLayer: InspectLayer): InspectLayer[] {
    let { parent } = selectedLayer;
    const ancestors = [selectedLayer];

    while (parent) {
        ancestors.push(parent);
        ({ parent } = parent);
    }

    return ancestors;
}

function findComponentByPredicate(
    { componentSections }: ComponentSectionsContainer,
    predicate: (c: Component) => boolean,
    ancestors: ComponentSection[] = []
): ComponentQueryResult | null {
    if (!componentSections) {
        return null;
    }

    for (const componentSection of componentSections) {
        const component = componentSection.components?.find(comp => predicate(comp));
        if (component) {
            return {
                ancestors: [...ancestors, componentSection],
                component
            };
        }

        const componentQueryResult = findComponentByPredicate(
            componentSection,
            predicate,
            [...ancestors, componentSection]
        );
        if (componentQueryResult) {
            return componentQueryResult;
        }
    }

    return null;
}

function findComponentSectionById(
    { componentSections }: ComponentSectionsContainer,
    csid: string
): ComponentSection | null {
    for (const componentSection of componentSections!) {
        if (componentSection._id === csid) {
            return componentSection;
        }

        const componentSectionQueryResult = findComponentSectionById(componentSection, csid);
        if (componentSectionQueryResult) {
            return componentSectionQueryResult;
        }
    }

    return null;
}

function searchComponentsForLayers(
    layers: InspectLayer[],
    componentsData: BarrelLike[]
): LayerComponentSearchResult | null {
    for (const layer of layers) {
        const { componentName, componentId } = layer;
        if (!componentName && !componentId) {
            continue;
        }

        const componentQueryResult = findComponentByNameAndSourceId(componentsData, componentName, componentId);
        if (!componentQueryResult) {
            continue;
        }

        return {
            layer,
            ...componentQueryResult
        };
    }

    return null;
}

function searchVirtualComponentsForLayers(
    layers: InspectLayer[],
    componentsData: BarrelLike[]
): InspectLayer | null {
    for (const layer of layers) {
        const { componentName, componentId } = layer;
        if (!componentName && !componentId) {
            continue;
        }

        const componentQueryResult = findComponentByNameAndSourceId(componentsData, componentName, componentId);
        if (!componentQueryResult) {
            return layer;
        }
    }

    return null;
}

function findComponentByNameAndSourceId(
    barrels: BarrelLike[],
    name?: string,
    sourceId?: string
): ComponentSearchResult | null {
    if (!name && !sourceId) {
        return null;
    }

    if (sourceId) {
        for (const barrel of barrels) {
            const componentQueryResult = findComponentByPredicate(barrel,
                c => c.sourceId === sourceId
            );

            if (componentQueryResult) {
                return {
                    barrelId: barrel.id,
                    ...componentQueryResult
                };
            }
        }
    }

    if (name) {
        for (const barrel of barrels) {
            const componentQueryResult = findComponentByPredicate(barrel,
                c => c.name === name
            );

            if (componentQueryResult) {
                return {
                    barrelId: barrel.id,
                    ...componentQueryResult
                };
            }
        }
    }

    return null;
}

function findComponentById(
    barrels: BarrelLike[],
    coid: string | null
): ComponentSearchResult | null {
    if (!coid) {
        return null;
    }

    for (const barrel of barrels) {
        const componentQueryResult = findComponentByPredicate(barrel, c => c._id === coid);

        if (componentQueryResult) {
            return {
                barrelId: barrel.id,
                ...componentQueryResult
            };
        }
    }

    return null;
}

function getComponentSectionById(
    barrels: ComponentSectionsContainer[],
    id: string
): ComponentSection | null {
    if (!id) {
        return null;
    }

    for (const barrel of barrels) {
        const componentSection = findComponentSectionById(barrel, id);

        if (componentSection) {
            return componentSection;
        }
    }

    return null;
}

function findComponentLayer(
    selectedLayer: InspectLayer | null | undefined,
    componentsData: BarrelLike[] | null | undefined
): LayerComponentSearchResult | null {
    if (!selectedLayer || !componentsData) {
        return null;
    }

    const ancestors = findAncestors(selectedLayer);
    if (!ancestors) {
        return null;
    }

    return searchComponentsForLayers(ancestors, componentsData);
}

function findVirtualComponentLayer(
    selectedLayer: InspectLayer | null | undefined,
    componentsData: BarrelLike[] | null | undefined
): InspectLayer | null {
    if (!selectedLayer || !componentsData) {
        return null;
    }

    const ancestors = findAncestors(selectedLayer);
    if (!ancestors) {
        return null;
    }

    return searchVirtualComponentsForLayers(ancestors, componentsData);
}

function isBooleanProperty(values: string[]): boolean {
    const booleanPropertyArrays = [
        ["true", "false"],
        ["yes", "no"],
        ["on", "off"]
    ];

    for (const booleanProperties of booleanPropertyArrays) {
        if (values.every(value => booleanProperties.includes(value.toLowerCase()))) {
            return true;
        }
    }

    return false;
}

function isTruthyPropertyValue(value: string): boolean {
    return ["true", "yes", "on"].includes(value.toLowerCase());
}

function findOmittedVariantProperties(components: Component[]): string[] {
    const valueMapping: BasicRecord<number> = {};

    components.forEach(({ variant }) => {
        if (!variant) {
            return;
        }

        variant.values.forEach(({ propertyId, value }) => {
            const key = `${propertyId}:${value}`;

            if (key in valueMapping) {
                valueMapping[key]++;
            } else {
                valueMapping[key] = 1;
            }
        });
    });

    return Object.entries(valueMapping)
        .filter(([, count]) => count === components.length)
        .map(([key]) => key);
}

function getComponentLayerForVariant(
    selectedLayer: InspectLayer | null | undefined,
    componentsData: BarrelLike[]
): LayerComponentSearchResult | null {
    const componentQueryResult = findComponentLayer(selectedLayer, componentsData);

    if (!componentQueryResult) {
        return null;
    }

    // Alter component name as section name if it has variants
    const ancestorWithVariant = componentQueryResult.ancestors.find(ancestor => ancestor.variant);

    if (!ancestorWithVariant) {
        return componentQueryResult;
    }

    return {
        ...componentQueryResult,
        component: {
            ...componentQueryResult.component,
            variantSectionName: ancestorWithVariant.name
        }
    };
}

function filterComponentSections({
    sections,
    filterValue
}: {
    sections: ComponentSectionResult[];
    filterValue: string;
}) {
    return sections.map(section => {
        const doesSectionMatch = calculateMatchingScore({
            searchTerm: filterValue,
            target: section.name,
            enableWordSearch: true
        });

        if (doesSectionMatch) {
            return section;
        }

        const matchedSubItems =
            (section.componentSections).filter(subItem => {
                if (!subItem.name) {
                    return false;
                }

                return calculateMatchingScore({
                    searchTerm: filterValue,
                    target: subItem.name,
                    enableWordSearch: true
                });
            });

        if (matchedSubItems.length) {
            return {
                ...section,
                componentSections: matchedSubItems
            };
        }

        return null;
    }).filter(exists);
}

function filterComponents(componentSections: ComponentSection[], filterValue: string) {
    const components = collectComponents(componentSections);
    const scores = new WeakMap<Component, number>(
        components.map((component: Component) => [component, calculateMatchingScore({
            searchTerm: filterValue,
            target: component.name
        })])
    );

    const filteredComponents = components.filter((component: Component) => scores.get(component));

    if (!filteredComponents.length) {
        return [];
    }

    const [selectedComponentSection] = componentSections;
    const transformedComponents = filteredComponents.sort((first: Component, second: Component) => {
        const diff = scores.get(second)! - scores.get(first)!;

        if (!diff) {
            return sortBy.name(first, second);
        }

        return diff;
    });

    return [
        {
            ...selectedComponentSection,
            components: transformedComponents
        }
    ];
}

function doesPatternMatchComponentName(pattern: string, componentName: string) {
    if (pattern === componentName) {
        return true;
    }

    let result = false;
    let maxNumberOfCharactersMatched = 0;

    if (new RegExp(`^${escapeRegExpButAsterisk(pattern)}$`.replace(/\*/g, ".*")).test(componentName)) {
        const numberOfCharactersMatched = pattern.replace(/\*/g, "").length;

        if (!result || numberOfCharactersMatched > maxNumberOfCharactersMatched) {
            maxNumberOfCharactersMatched = numberOfCharactersMatched;
            result = true;
        }
    }

    return result;
}

function findUsedScreens(
    screens: (Screen | PartialScreen)[],
    componentName: string,
    componentId: string
): (Screen | PartialScreen)[] {
    return screens.filter(({
        versions: [{
            snapshot: { componentNames, componentIds } = { componentNames: undefined, componentIds: undefined }
        } = {}] = []
    }) => componentIds?.includes(componentId) || componentNames?.includes(componentName));
}

function findUsedScreensOfComponents(screens: Screen[], components: Component[]) {
    const componentIds = components.map(({ _id }) => _id);
    const componentNames = components.map(({ name }) => name);

    return screens
        .filter(({ versions: [{ snapshot: {
            componentNames: componentNamesInScreen, componentIds: componentIdsInScreen
        } = { componentNames: undefined, componentIds: undefined } } = {}] }) => (
            (componentIdsInScreen && componentIds.some(componentId => componentIdsInScreen.includes(componentId))) ||
            (componentNamesInScreen && componentNames.some(
                componentName => componentNamesInScreen.includes(componentName)
            ))
        ));
}

function findConnectedComponentsByIdOrName(
    componentId: string,
    componentName: string | null | undefined,
    barrels: StyleguideData[] | null | undefined,
    connectedComponentsDevModeEnabled: boolean
): ConnectedComponent[] {
    if (!componentName || !barrels) {
        return [];
    }

    const result: ConnectedComponent[] = [];

    for (const barrel of barrels) {
        const connectedComponents = connectedComponentsDevModeEnabled
            ? barrel.localConnectedComponents
            : barrel.connectedComponents;
        if (!connectedComponents) {
            continue; // eslint-disable-line no-continue
        }

        for (const connectedComponent of connectedComponents) {
            const doesExist = result.find(item => item._id === connectedComponent._id);

            if (!doesExist && (
                connectedComponent.componentId === componentId ||
                (connectedComponent.pattern && doesPatternMatchComponentName(connectedComponent.pattern, componentName))
            )) {
                result.push(connectedComponent);
            }
        }
    }

    return result;
}

interface LinkedStoryPath {
    bid: string;
    barrelType: BarrelType;
    storyPath: string;
}

function getComponentLinkedStoryPaths(styleguidesData: StyleguideData[], coid: string): LinkedStoryPath[] {
    const linkedStoryPaths: LinkedStoryPath[] = [];

    for (const styleguideData of styleguidesData) {
        for (const connectedComponent of styleguideData.connectedComponents ?? []) {
            const { componentId, links = [] } = connectedComponent;

            if (componentId === coid) {
                const storyPaths = links.filter(({ type, metadata }) => type === "storybook" && Boolean(metadata))
                    .map(({ metadata }) => ({
                        bid: styleguideData.id,
                        barrelType: styleguideData.barrelType,
                        storyPath: `${metadata!.storybook!.kind}/${metadata!.storybook!.story}`
                    }));

                linkedStoryPaths.push(...storyPaths);
            }
        }
    }

    return linkedStoryPaths;
}

function getComponentStyleguide(coid: string, barrels: StyleguideData[]): StyleguideData | undefined {
    return barrels.find(barrel =>
        !!findComponentByPredicate(barrel, c => c._id === coid)
    );
}

function getAllComponentsOfComponentVariantSection(componentSection: ComponentSection): Component[] {
    const result: Component[] = [];
    result.push(...componentSection.components);

    componentSection.componentSections.forEach(section => {
        result.push(...getAllComponentsOfComponentVariantSection(section));
    });

    return result;
}

function tranformVariantProperties(
    componentVariant?: ComponentVariant,
    sectionVariant?: ComponentSectionVariant
): LayerDetailComponentVariantProperty[] | null {
    if (!componentVariant || !sectionVariant) {
        return null;
    }

    return componentVariant.values.map(({ propertyId, value }) => {
        const property = sectionVariant.properties.find(({ _id }) => _id === propertyId);

        if (!property) {
            return null;
        }

        return {
            id: propertyId,
            name: property.name,
            value
        };
    }).filter(Boolean) as LayerDetailComponentVariantProperty[];
}

function getAllComponentsOfComponentLayers(
    componentLayers: InspectLayer[],
    componentsData: BarrelLike[]
): BasicComponentWithLayer[] {
    return componentLayers.reduce<BasicComponentWithLayer[]>((acc, componentLayer) => {
        const { componentName, componentId, name } = componentLayer;
        if (!componentId) {
            return acc;
        }

        const foundComponent = findComponentByNameAndSourceId(componentsData, undefined, componentId);
        if (foundComponent) {
            const ancestorWithVariant = foundComponent.ancestors.find(ancestor => ancestor.variant);
            acc.push({
                sourceId: componentId,
                componentId: foundComponent.component._id,
                componentName: ancestorWithVariant ? ancestorWithVariant.name : foundComponent.component.name,
                componentLayer
            });
        } else if (componentId) {
            acc.push({
                sourceId: componentId,
                componentName: componentName ?? name,
                componentLayer
            });
        }

        return acc;
    }, []);
}

function getLayerFromComponentLayer(componentLayer: InspectLayer): InspectLayer {
    if (!componentLayer.layers?.length) {
        return componentLayer;
    }

    let foundLayer = null;
    componentLayer.layers.forEach(layer => {
        if (!shallowDiffer(componentLayer.absoluteRect, layer.absoluteRect)) {
            foundLayer = layer;
        }
    });

    return foundLayer ? getLayerFromComponentLayer(foundLayer) : componentLayer;
}

function isStyleguideComponent(coid: string, barrel: BarrelLike): boolean {
    return !!findComponentByPredicate(barrel, c => c._id === coid);
}

function findLatestComponent(componentSections?: ComponentSection[]): Component | null {
    if (!componentSections) {
        return null;
    }

    let latestComponent: Component | null | undefined;
    for (const section of componentSections) {
        for (const component of section.components) {
            if (!latestComponent || component.latestVersion.created > latestComponent!.latestVersion.created) {
                latestComponent = component;
            }
        }

        for (const group of section.componentSections) {
            for (const component of group.components) {
                if (!latestComponent || component.latestVersion.created > latestComponent.latestVersion.created) {
                    latestComponent = component;
                }
            }
        }
    }

    return latestComponent ?? null;
}

function collectIcons(sections: ComponentSection[] = []): Component[] {
    return ([] as Component[])
        .concat(
            ...sections.filter(({ type }) => type === ComponentPageType.icon).map(sec => (
                sec?.components?.concat(collectComponents(sec.componentSections))
            ))
        );
}

function collectComponents(sections: ComponentSection[] = []): Component[] {
    return ([] as Component[])
        .concat(...sections.map(sec => sec?.components?.concat(collectComponents(sec.componentSections))));
}

function isComponentSmallSize(imageWidth: number, imageHeight: number) {
    return imageWidth <= SMALL_SIZE_COMPONENT_WIDTH && imageHeight <= SMALL_SIZE_COMPONENT_HEIGHT;
}

// this method filters out groups from left panel view.
function transformComponentSectionTreeView(cs: ComponentSection) {
    if (!cs) {
        return [];
    }

    // if it's a legacy page or it has single section with same name we do not display on tree view
    if (
        cs.type === ComponentPageType.legacy ||
        cs.componentSections.length === 1 && cs.componentSections[0].name === cs.name
    ) {
        return [];
    }

    // custom pages' first second level componentSections array indicates groups
    return cs.componentSections.map(pageSection => ({
        ...pageSection,
        componentSections: []
    }));
}

// Now we're always displaying individual components instead of default
function getComponentSectionsInLeftPanel(
    styleguideData: StyleguideData,
    _options: { isSyncedWithFigma: boolean; }
): ComponentSectionWithDefaultInfo[] {
    const { componentSections: componentSectionsRaw = [] } = styleguideData;
    const [defaultSection, ...componentSections] = componentSectionsRaw;

    const componentSectionsTransformed = [...componentSections]
        .map(section => ({
            ...section,
            componentSections: transformComponentSectionTreeView(section)
        }))
        .sort(sortBy.name);
    const defaultSectionTransformed = {
        ...defaultSection,
        isDefaultSection: true,
        name: INDIVIDUAL_COMPONENTS_SECTION_NAME,
        description: INDIVIDUAL_COMPONENTS_SECTION_DESCRIPTION,
        componentSections: transformComponentSectionTreeView(defaultSection)
    };

    return [
        defaultSectionTransformed,
        ...componentSectionsTransformed
    ];
}

function splitComponentProperties(properties: ComponentProperty[] | undefined): {
    defaultProperties: ComponentProperty[];
    slotProperties: ComponentProperty[];
} {
    const defaultProperties: ComponentProperty[] = [];
    const slotProperties: ComponentProperty[] = [];

    if (properties) {
        for (const property of properties) {
            if (property.type === "slot") {
                slotProperties.push(property);
            } else {
                defaultProperties.push(property);
            }
        }
    }

    return { defaultProperties, slotProperties };
}

export {
    LAYER_COMPONENT_VIEW_LIMIT,
    HIGHLIGHTED_CONNECT_TO_CODE_VIEW_LIMIT,
    LayerComponentSearchResult,
    ComponentQueryResult,
    SearchedComponent,
    ComponentSearchResult,
    LinkedStoryPath,
    findComponentById,
    findComponentSectionById,
    findComponentLayer,
    findVirtualComponentLayer,
    isBooleanProperty,
    isTruthyPropertyValue,
    findOmittedVariantProperties,
    getComponentSectionById,
    getComponentLayerForVariant,
    filterComponentSections,
    filterComponents,
    doesPatternMatchComponentName,
    findUsedScreens,
    findUsedScreensOfComponents,
    findConnectedComponentsByIdOrName,
    getComponentLinkedStoryPaths,
    getComponentStyleguide,
    getAllComponentsOfComponentVariantSection,
    getAllComponentsOfComponentLayers,
    getLayerFromComponentLayer,
    isStyleguideComponent,
    flattenComponents,
    flattenComponentSections,
    getOverriddenPreviewBackgroundColorChildren,
    tranformVariantProperties,
    findLatestComponent,
    collectComponents,
    collectIcons,
    isComponentSmallSize,
    getComponentSectionsInLeftPanel,
    splitComponentProperties
};
